/**
 * Permission Hooks
 *
 * React hooks for accessing permissions in components.
 */

import { useMemo } from "react";

import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";

import {
  canAccessAdmin,
  canAccessDonorLab,
  canCreateClient,
  canDeleteClient,
  canEditBrand,
  canEditClient,
  canImportData,
  canManageUsers,
  canViewAdmin,
  canViewBrandHub,
  canViewClient,
  canViewDonorLab,
  getPermissionSummary,
  isAdmin,
  isManager,
  isOwner,
  isSuperAdmin,
} from "./checks";

// =============================================================================
// PERMISSION HOOKS
// =============================================================================

/**
 * Hook to get all permission checks for the current user and client.
 *
 * Usage:
 * ```tsx
 * const { canEdit, canViewAdmin } = usePermissions();
 * ```
 */
export function usePermissions() {
  const { user } = useAuth();
  const { currentClient } = useClient();

  return useMemo(
    () => ({
      // Role checks
      isAdmin: isAdmin(user),
      isOwner: isOwner(user),
      isManager: isManager(user),

      // View permissions
      canViewClient: canViewClient(user, currentClient),
      canViewAdmin: canViewAdmin(user),
      canViewBrandHub: canViewBrandHub(user, currentClient),
      canViewDonorLab: canViewDonorLab(user, currentClient),

      // Edit permissions
      canEditClient: canEditClient(user, currentClient),
      canEditBrand: canEditBrand(user, currentClient),
      canImportData: canImportData(user, currentClient),
      canManageUsers: canManageUsers(user),

      // Administrative
      canAccessAdmin: canAccessAdmin(user),
      canCreateClient: canCreateClient(user),
      canDeleteClient: canDeleteClient(user, currentClient),

      // Module access
      canAccessDonorLab: canAccessDonorLab(user, currentClient),

      // Debug
      summary: getPermissionSummary(user, currentClient),
    }),
    [user, currentClient],
  );
}

/**
 * Hook to check if user can edit the current client.
 *
 * Usage:
 * ```tsx
 * const canEdit = useCanEdit();
 * if (canEdit) { ... }
 * ```
 */
export function useCanEdit(): boolean {
  const { user } = useAuth();
  const { currentClient } = useClient();

  return useMemo(
    () => canEditClient(user, currentClient),
    [user, currentClient],
  );
}

/**
 * Hook to check if user can access admin.
 */
export function useCanAccessAdmin(): boolean {
  const { user } = useAuth();
  return useMemo(() => canAccessAdmin(user), [user]);
}

/**
 * Hook to check if user is admin.
 */
export function useIsAdmin(): boolean {
  const { user } = useAuth();
  return useMemo(() => isAdmin(user), [user]);
}

/**
 * Hook to check if user is super admin (platform owner).
 */
export function useIsSuperAdmin(): boolean {
  const { user } = useAuth();
  return useMemo(() => isSuperAdmin(user), [user]);
}
